import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import formatCurrency from "../../utils/numberFormatter";
import { stripLinkquLabel } from "../utils/paymentMethodDisplay";
import useTranslation from "../hooks/useTranslation";

const formatAmount = (amount) => formatCurrency(amount || 0).split(",")[0];

const formatSuccessDate = (dateString) => {
  if (!dateString || dateString === "-") return "-";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(":", ".");

  return `${datePart}, ${timePart}`;
};

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const paymentData = location.state || {};
  const amount = formatAmount(paymentData.amount);
  const date = formatSuccessDate(paymentData.paymentDate);
  const paymentMethod = stripLinkquLabel(paymentData.paymentMethod) || "-";
  const reffNo = paymentData.reffNo || "-";

  useEffect(() => {
    if (!paymentData.redirectUrl) return undefined;

    const redirectTimer = window.setTimeout(() => {
      window.location.href = paymentData.redirectUrl;
    }, 10000);

    return () => window.clearTimeout(redirectTimer);
  }, [paymentData.redirectUrl]);

  useEffect(() => {
    if (!paymentData?.reffNo) {
      navigate("/", { replace: true });
    }
  }, [paymentData]);

  // Prevent the browser back button from returning into the completed payment
  // flow (SecondForm/ThirdForm) — send the user to the app's root instead.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      navigate("/", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  return (
    <main className="flex min-h-screen items-start justify-center bg-[#f5f5f5] px-6 py-14 md:items-center md:py-20">
      <div className="flex w-full max-w-[640px] flex-col items-center gap-8">
        <section className="w-full rounded-[28px] bg-white px-6 py-10 text-center shadow-[0_10px_22px_rgba(15,23,42,0.08)] md:px-16">
          <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-full bg-[#E9FFF3]">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#D2FBE5]">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-singaHijauPrimary text-white">
                <Check size={28} strokeWidth={3.5} aria-hidden="true" />
              </div>
            </div>
          </div>

          <h1 className="mt-7 text-2xl font-semibold text-gray-900">{t("paymentSuccess.title")}</h1>
          <p className="mx-auto mt-3 max-w-[500px] text-base font-normal leading-7 text-gray-600">
            {t("paymentSuccess.orderPaidPrefix")}
            <span className="break-all">{reffNo}</span> {t("paymentSuccess.orderPaidSuffix")}
          </p>

          <div className="my-6 border-t border-dashed border-gray-300" />

          <dl className="mx-auto grid max-w-[500px] grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-x-4 gap-y-4 text-left">
            <dt className="text-base font-normal text-gray-400">{t("paymentSuccess.amountPaid")}</dt>
            <dd className="text-right text-base font-semibold text-gray-900">{amount}</dd>

            <dt className="text-base font-normal text-gray-400">{t("paymentSuccess.datePaid")}</dt>
            <dd className="text-right text-base font-normal text-gray-900">{date}</dd>

            <dt className="text-base font-normal text-gray-400">{t("paymentSuccess.paymentMethod")}</dt>
            <dd className="text-right text-base font-normal text-gray-900">{paymentMethod}</dd>
          </dl>
        </section>

        <div className="flex w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
          <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBy")}</span>
          <img src="/assets/images/logo-singapay-horizontal.png" alt="SingaPay" className="h-6 w-auto object-contain" />
          {t("layout.poweredBySuffix") && (
            <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBySuffix")}</span>
          )}
        </div>
      </div>
    </main>
  );
}
